/**
 * ClaimPioneerPage — landing for the magic-link in the Pioneer invite
 * email (iter191). Users arrive here from the email button; if they're
 * not signed in we redirect them through /login and come back; if they
 * ARE signed in we POST the token + show success.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClaimPioneerPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [status, setStatus] = useState('idle'); // idle | claiming | success | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Already on /login (e.g. effect re-fired after navigate) — bail out
      // so we don't double-wrap the next= param.
      if (window.location.pathname.startsWith('/login')) return;
      // Send them through login, come back here after.
      navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (!token || status !== 'idle') return;
    setStatus('claiming');
    axios.post(`${API}/mobile-waitlist/pioneer-claim/${encodeURIComponent(token)}`)
      .then(() => setStatus('success'))
      .catch((err) => {
        setError(err?.response?.data?.detail || 'We could not claim that invite.');
        setStatus('error');
      });
  }, [user, authLoading, token, navigate, status]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background:
          'radial-gradient(900px 500px at 50% 0%, rgba(0,47,167,0.18) 0%, transparent 60%),' +
          'linear-gradient(180deg, #F5F7FB 0%, #E8EEFB 100%)',
        color: '#0F172A',
      }}
      data-testid="claim-pioneer-page"
    >
      <div className="w-full max-w-md bg-white/80 backdrop-blur-xl border border-white/60 rounded-3xl shadow-[0_20px_60px_-20px_rgba(0,47,167,0.25)] p-7 sm:p-9 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#FFD700] to-[#F59E0B] flex items-center justify-center mb-5 shadow-lg">
          <Crown className="w-8 h-8 text-white" aria-hidden />
        </div>
        {status === 'claiming' && (
          <>
            <h1 className="font-serif-accent text-3xl font-bold mb-2">Claiming your Pioneer status…</h1>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-[#002FA7]" aria-hidden />
          </>
        )}
        {status === 'success' && (
          <>
            <h1 className="font-serif-accent text-3xl font-bold mb-2">You&apos;re a Pioneer.</h1>
            <p className="text-sm text-slate-600 mb-6">Every paid feature is unlocked on your account — for life. A gold crown will now appear next to your avatar.</p>
            <div className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-semibold mb-6">
              <CheckCircle2 className="w-4 h-4" aria-hidden /> Founding Member access granted
            </div>
            <Link to="/app" data-testid="claim-pioneer-go-to-app">
              <Button className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white gap-2">
                Open iStudyPro <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="font-serif-accent text-2xl font-bold mb-2">We couldn&apos;t claim that invite.</h1>
            <p className="text-sm text-slate-600 mb-5">{error}</p>
            <Link to="/app">
              <Button variant="outline" className="w-full">Back to the app</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimPioneerPage;
